/**
 * ResignationBannerController — bandeau permanent §25.B + §25.D
 * (EPIC-037 / 37.3).
 *
 * Intégré sur /tableau-de-bord.html, /profil-utilisateur.html et
 * /profil-entreprise.html. Lit `getLifecycle()` au connect() :
 *   - si `lifecycleStatus !== 'resiliation_pending'` → masque tout.
 *   - sinon → affiche le bandeau, remplit la date et le compteur
 *     d'en-cours, expose deux actions :
 *       · « Voir les en-cours » → ouvre la modale légère lecture seule
 *         (checklistDialog target, rebrousse la maquette 2).
 *       · « Annuler la résiliation » → confirm() puis
 *         POST `/organization/lifecycle/resignation/cancel` + reload.
 *
 * Best-effort : si l'appel API échoue, le bandeau reste caché (la page
 * applicative continue de fonctionner — pas de blocage).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import confirm from "/js/services/confirm.js";

export default class ResignationBannerController extends BaseController {
  static targets = [
    "banner",
    "dateSlot",
    "checklistCountSlot",
    "checklistDialog",
    "checklistItem",
    "cancelBtn",
  ];

  async connect() {
    this._hide();
    try {
      const state = await api.getLifecycle();
      if (state?.lifecycleStatus === "resiliation_pending") {
        this._fillSlots(state);
        this._show();
      }
    } catch {
      // Best-effort : page applicative reste fonctionnelle.
    }
  }

  openChecklist(event) {
    if (event) event.preventDefault();
    if (
      this.hasChecklistDialogTarget
      && typeof this.checklistDialogTarget.showModal === "function"
    ) {
      this.checklistDialogTarget.showModal();
    }
  }

  closeChecklist(event) {
    if (event) event.preventDefault();
    if (
      this.hasChecklistDialogTarget
      && typeof this.checklistDialogTarget.close === "function"
    ) {
      this.checklistDialogTarget.close();
    }
  }

  async cancel(event) {
    if (event) event.preventDefault();
    const confirmed = await confirm(
      "Confirmez-vous l'annulation ? Votre abonnement Notamicro restera actif. Un avoir éventuel reste disponible si le forfait d'archivage a déjà été payé.",
      {
        title: "Annuler la résiliation",
        confirmLabel: "Confirmer l'annulation",
        cancelLabel: "Garder la résiliation",
      },
    );
    if (!confirmed) return;

    if (this.hasCancelBtnTarget) this.cancelBtnTarget.disabled = true;
    try {
      await api.cancelResignation();
      this.showSuccess("Résiliation annulée. Votre abonnement reste actif.");
      window.location.reload();
    } catch (error) {
      const message = error && error.message
        ? error.message
        : "Impossible d'annuler la résiliation.";
      this.showError(message);
      if (this.hasCancelBtnTarget) this.cancelBtnTarget.disabled = false;
    }
  }

  _fillSlots(state) {
    if (this.hasDateSlotTarget) {
      const date = state.resignationScheduledAt
        ?? state.resiliationScheduledAt
        ?? null;
      this.dateSlotTarget.textContent = date
        ? this.formatDate(date)
        : "Fin de la période en cours";
    }
    const items = state.checklistItems ?? null;
    // EPIC-099 / 99.37 — 6 catégories fixes (activeSepaMandates retiré,
    // mandat SEPA URSSAF hors périmètre Notamicro).
    const total = items
      ? [
          items.recurrences,
          items.unpaidInvoices,
          items.acceptedQuotes,
          items.pendingDeclarations,
          items.activeMandates,
          items.publicDocuments,
        ].reduce((acc, entry) => acc + this._entryCount(entry), 0)
      : 0;
    if (this.hasChecklistCountSlotTarget) {
      this.checklistCountSlotTarget.textContent = total === 0
        ? "Aucun"
        : String(total);
    }
    if (this.hasChecklistItemTarget && items) {
      this._setChecklistItem("recurrences", items.recurrences);
      this._setChecklistItem("unpaidInvoices", items.unpaidInvoices);
      this._setChecklistItem("acceptedQuotes", items.acceptedQuotes);
      this._setChecklistItem("pendingDeclarations", items.pendingDeclarations);
      this._setChecklistItem("activeMandates", items.activeMandates);
      this._setChecklistItem("publicDocuments", items.publicDocuments);
    }
  }

  /**
   * EPIC-099 / 99.8 — chaque entrée `checklistItems.<key>` est désormais
   * un objet `{ count, label, … }` (libellé pré-formaté côté backend).
   * Fallback `typeof value === "number"` conservé pour la phase de bascule
   * (anciens clients ou payloads cachés en mémoire).
   */
  _setChecklistItem(key, value) {
    const el = this.checklistItemTargets.find(
      (target) => target.dataset.key === key,
    );
    if (!el) return;
    if (value && typeof value === "object") {
      const count = Number(value.count ?? 0);
      if (count === 0) {
        el.textContent = "Aucun";
      } else {
        el.textContent = value.label || String(count);
      }
      return;
    }
    const n = typeof value === "number" ? value : 0;
    el.textContent = n === 0 ? "Aucun" : String(n);
  }

  _entryCount(entry) {
    if (entry && typeof entry === "object") return Number(entry.count ?? 0);
    return typeof entry === "number" ? entry : 0;
  }

  _show() {
    if (this.hasBannerTarget) this.bannerTarget.hidden = false;
  }

  _hide() {
    if (this.hasBannerTarget) this.bannerTarget.hidden = true;
  }
}
